import React from 'react';
import {Box, Typography} from '@mui/material';
import {BuilderPot, BuilderPot_dentistChildren} from '../../../schema-types/BuilderPot';
import {PlanAccordionShell} from '../CreateBuilder/BuilderSourceDestination/PlanAccordionShell';
import {PlanSelector} from '../CreateBuilder/BuilderSourceDestination/PlanSelector';
import {PlanValidationIcon} from '../CreateBuilder/BuilderSourceDestination/PlanValidationIcon';
import {usePlanValidationLevel} from '../CreateBuilder/BuilderSourceDestination/usePlanValidationLevel';
import {
  getSummary,
  getSummaryParts,
} from '../CreateBuilder/BuilderSourceDestination/builderNaming';
import {getCurrencyFloat, getCurrencyString} from '../../../utils/currencyFormatter';
import {builderFieldsType} from '../CreateBuilder/builderFields';
import {useAutoForm} from '../../../components/AutoForm/useAutoForm';
import {ContributeDestinationSubplan} from './ContributeDestinationSubplan';
import {BuilderContributeAmounts} from './BuilderContributeAmounts';
import {ParentPlanRow} from '../CreateBuilder/BuilderSourceDestination/ParentPlanRow';

const PostContributionText: React.FC<{pot: BuilderPot}> = ({pot}) => {
  const {values} = useAutoForm<builderFieldsType>();
  const contributeTo = values.contribute?.contributeTo;
  if (pot.id !== contributeTo) return null;
  return (
    <Typography variant="body2">
      <strong data-testid="post-contribution-id">{`Post Initial Contribution: ${getCurrencyString(
        getCurrencyFloat(pot.totalValue) +
          getCurrencyFloat(values.contribute?.contributeFrom?.singleClientAmount || 0)
      )}`}</strong>
    </Typography>
  );
};

interface ContributeDestinationParentProps {
  pot: BuilderPot;
  contributeToId: string | undefined | null;
  fixed?: boolean;
  sameReview?: boolean;
  handleChange: (pot: BuilderPot | BuilderPot_dentistChildren) => void;
}

export const ContributeDestinationParent: React.FC<ContributeDestinationParentProps> = ({
  pot,
  contributeToId,
  fixed,
  sameReview,
  handleChange,
}) => {
  const {values} = useAutoForm<builderFieldsType>();
  const dentistChildren: BuilderPot_dentistChildren[] = pot.dentistChildren ?? [];
  const hasChildren = dentistChildren.length > 0;
  const isSelected = pot.id === contributeToId;
  const isDirectlySelected = !hasChildren && isSelected;
  const existingPotId = values.contribute?.destinationValues?.existingPotId;
  const childSelected =
    hasChildren && dentistChildren.some((c) => c.id === existingPotId);
  const activeFormikKey =
    isDirectlySelected || childSelected ? 'contribute.destinationValues' : undefined;
  const {bgClass} = usePlanValidationLevel(activeFormikKey);

  if (hasChildren) {
    const {label, currentValue} = getSummaryParts(pot);
    return (
      <ParentPlanRow
        pot={pot}
        isSelected={childSelected}
        sameReview={sameReview}
        validationLevel={bgClass}
        summaryChildren={
          <>
            <Typography variant="body1" sx={{fontWeight: 600}}>
              {label}
            </Typography>
            <Box display="flex" flexDirection="column" alignItems="flex-end" sx={{ml: 2}}>
              {currentValue && (
                <Typography variant="body2" sx={{fontWeight: 600}}>
                  {currentValue}
                </Typography>
              )}
              <PostContributionText pot={pot} />
            </Box>
          </>
        }
        summaryTrailing={
          childSelected && activeFormikKey ? (
            <PlanValidationIcon formikKeyPrefix={activeFormikKey} />
          ) : undefined
        }
      >
        {dentistChildren.map((child) => (
          <ContributeDestinationSubplan
            key={child.id}
            subPlan={child}
            isSelected={existingPotId === child.id}
            handleChange={() => handleChange(child)}
          />
        ))}
      </ParentPlanRow>
    );
  }

  return (
    <ParentPlanRow pot={pot} isSelected={isDirectlySelected} sameReview={sameReview}>
      <PlanAccordionShell
        expanded={isDirectlySelected}
        isSelected={isDirectlySelected}
        isSoleParent
        validationLevel={bgClass}
        sameReview={sameReview}
        summaryChildren={
          <>
            <PlanSelector
              mode="single"
              id={pot.id}
              isSelected={isDirectlySelected}
              fixed={fixed}
              handleChange={() => handleChange(pot)}
            />
            <Box display="flex" flexDirection="column">
              {getSummary(pot)}
              <PostContributionText pot={pot} />
            </Box>
          </>
        }
        summaryTrailing={
          activeFormikKey ? (
            <PlanValidationIcon formikKeyPrefix={activeFormikKey} />
          ) : undefined
        }
      >
        <BuilderContributeAmounts disabled />
      </PlanAccordionShell>
    </ParentPlanRow>
  );
};
